// `/registrer?kode=…` — den offentlige inngangen for nye frivillige.
//
// Denne siden er den eneste i appen som kjører UTEN token, og den er det første
// en ny bruker ser av hele programmet. Tre ting er derfor viktigere her enn
// noe annet sted:
//
//  1. Den skal ALDRI krasje. En utløpt eller oppbrukt lenke er en helt vanlig
//     hendelse (lenker varer 7 døgn og er som regel engangs), ikke en feil. Da
//     skal siden si det rolig og fortelle hva man gjør videre.
//  2. Tokenet vises ÉN gang. Tjenesten lagrer det hashet og kan ikke hente det
//     fram igjen. Skjermen må si det med tydelige ord FØR brukeren lukker fanen.
//  3. Ansattnummeret sendes hit og forsvinner. Det lagres ikke i nettleseren,
//     legges ikke i URL-en og vises ikke igjen etterpå.
import type { Klient } from '../api/klient.js';
import type { ApiFeil } from '../api/feil.js';
import type { LenkeStatusSvar, RegistrertSvar } from '../api/typer.js';
import { avkrysning, div, felt, h, knapp, p, sett, span } from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { feilboks, laster, kvittering } from '../ui/tilstand.js';

export function visRegistrering(rot: HTMLElement, klient: Klient, kode: string): void {
  rot.className = 'reg-rot';
  const kort = div('reg-kort');
  sett(rot, kort);

  const merke = div('reg-merke');
  merke.append(span('reg-logo', 'PROSJEKT'), span('reg-under', 'Frivillighetstimer — Heimevernet'));
  kort.append(merke);

  const innhold = div('reg-innhold');
  kort.append(innhold);

  if (!kode) {
    sett(innhold, rolig(
      'Lenken mangler kode',
      'Registreringslenken skal se ut som «…/registrer?kode=…». Denne mangler kode-delen — '
      + 'sannsynligvis er den klippet feil da den ble limt inn. Be om lenken på nytt.',
    ));
    return;
  }

  void hentStatus();

  async function hentStatus(): Promise<void> {
    sett(innhold, laster('Kontrollerer lenken …', 2));
    const r = await klient.lenkeStatus(kode);
    if (!r.ok) return sett(innhold, feilboks(r.feil, () => void hentStatus()));
    const status = (r.data ?? {}) as LenkeStatusSvar;
    if (!status.gyldig) {
      // `grunn` kommer ordrett fra `kontrollerLenke()` — «utløpt» eller «brukt
      // opp». Den er skrevet for melderen, så vi viser den som den er.
      return sett(innhold, rolig(
        'Lenken kan ikke brukes',
        status.grunn ?? 'Lenken er ikke gyldig lenger.',
        'Be den som sendte deg lenken om en ny. Registreringslenker varer sju døgn, '
        + 'og de fleste er engangs.',
      ));
    }
    visSkjema(status);
  }

  function visSkjema(status: LenkeStatusSvar): void {
    sett(innhold);
    innhold.append(h(1, 'reg-tittel', 'Registrer deg'));
    innhold.append(p('reg-brød',
      'Du får et pseudonym og et personlig token. Pseudonymet er det andre ser; '
      + 'ansattnummeret ditt er det ikke.'));

    if (status.tilhorighet) {
      const rad = div('reg-tilhør');
      rad.append(ikon('info'), span('', `Tilhørighet fra lenken: ${status.tilhorighet}`));
      innhold.append(rad);
    }
    if (status.merknad) innhold.append(p('reg-merknad', status.merknad));

    const skjema = document.createElement('form');
    skjema.className = 'reg-skjema';
    skjema.noValidate = true;

    const ansattnummer = felt('Ansattnummer', {
      plassholder: '4–10 siffer',
      inndatamodus: 'numeric',
      påkrevd: true,
      hjelp: 'Koden Heimevernet fører timer på. Den lagres hos tjenesten, vises aldri '
        + 'til andre, og vises heller ikke i klartekst til deg senere.',
    });
    const etternavn = felt('Etternavn', { påkrevd: true, autofullfør: 'family-name' });
    const fornavn = felt('Fornavn', { påkrevd: true, autofullfør: 'given-name' });
    const medNavn = avkrysning(
      'Lagre navnet mitt sammen med pseudonymet',
      false,
    );
    const navnHjelp = p('felt-hjelp',
      'Av som standard. Uten dette lagres bare pseudonymet ditt, og navnet brukes '
      + 'kun til å gi deg riktig token nå.');

    const skjemafeil = div('skjema-feil');
    skjemafeil.setAttribute('role', 'alert');
    skjemafeil.hidden = true;

    const send = knapp('Registrer meg', undefined, { variant: 'primær', type: 'submit' });

    skjema.append(ansattnummer.rot, etternavn.rot, fornavn.rot, medNavn.rot, navnHjelp, skjemafeil, send);
    innhold.append(skjema);
    ansattnummer.input.focus();

    skjema.addEventListener('submit', (e) => {
      e.preventDefault();
      void send_();
    });

    async function send_(): Promise<void> {
      for (const f of [ansattnummer, etternavn, fornavn]) f.settFeil(null);
      skjemafeil.hidden = true;

      const kode2 = ansattnummer.input.value.trim();
      const en = etternavn.input.value.trim();
      const fn = fornavn.input.value.trim();
      // Samme regel som `AnsattnummerSchema`. Vi sjekker her for å gi svar med
      // en gang — tjenesten er fortsatt den som avgjør.
      if (!/^\d{4,10}$/.test(kode2)) {
        ansattnummer.settFeil('Ansattnummer skal være 4–10 siffer.');
        return ansattnummer.input.focus();
      }
      if (!en) { etternavn.settFeil('Fyll ut etternavn.'); return etternavn.input.focus(); }
      if (!fn) { fornavn.settFeil('Fyll ut fornavn.'); return fornavn.input.focus(); }

      send.disabled = true;
      const før = send.textContent;
      send.textContent = 'Registrerer …';
      const r = await klient.registrer(kode, {
        ansattnummer: kode2, etternavn: en, fornavn: fn, medNavn: medNavn.input.checked,
      });
      send.disabled = false;
      send.textContent = før;

      if (r.ok && r.data && typeof r.data === 'object' && 'token' in r.data) {
        return visKvittering(r.data as RegistrertSvar);
      }
      if (r.ok) {
        skjemafeil.hidden = false;
        skjemafeil.textContent = 'Tjenesten svarte uten token. Registreringen kan ha gått gjennom — '
          + 'prøv ikke på nytt før du har spurt den som sendte deg lenken.';
        return;
      }
      visSkjemafeil(r.feil);
    }

    function visSkjemafeil(feil: ApiFeil): void {
      const kart: Record<string, ReturnType<typeof felt>> = {
        ansattnummer, etternavn, fornavn,
      };
      const mål = feil.felt ? kart[feil.felt] : undefined;
      if (mål) {
        mål.settFeil(feil.melding);
        mål.input.focus();
        return;
      }
      skjemafeil.hidden = false;
      // Tjenestens 409-tekster («allerede brukt opp», «ansattnummeret er i bruk»)
      // sier ikke HVEM koden tilhører — bevisst, og vi legger ikke til noe.
      skjemafeil.textContent = feil.melding;
    }
  }

  function visKvittering(svar: RegistrertSvar): void {
    sett(innhold);
    innhold.append(h(1, 'reg-tittel', 'Du er registrert'));

    const advarsel = div('reg-advarsel');
    advarsel.setAttribute('role', 'alert');
    advarsel.append(ikon('varsel'));
    advarsel.append(p('', 'Tokenet under vises ÉN gang. Det er lagret hashet hos tjenesten og '
      + 'kan ikke hentes fram igjen. Lagre det i passordhvelvet før du lukker denne siden — '
      + 'mister du det, må du få en ny registreringslenke.'));
    innhold.append(advarsel);

    innhold.append(hemmelighetsrad('Pseudonym', svar.person.id, 'Dette er navnet andre ser på timene dine.'));
    innhold.append(hemmelighetsrad('Persontoken', svar.token, 'Lim dette inn på innloggingssiden.'));

    if (svar.person.tilhorighet) {
      innhold.append(p('reg-brød', `Tilhørighet: ${svar.person.tilhorighet}`));
    }
    if (typeof svar.tokenUtloperTsMs === 'number') {
      innhold.append(p('reg-brød', `Tokenet utløper ${new Date(svar.tokenUtloperTsMs).toLocaleString('nb-NO')}.`));
    }

    const videre = knapp('Til innlogging', () => {
      // Full navigasjon, ikke bare hash: vi forlater den offentlige ruta, og en
      // ren omlasting sikrer at ingenting fra registreringen henger igjen i
      // minnet i den innloggede appen.
      globalThis.location.href = '/';
    }, { variant: 'primær' });
    innhold.append(videre);
    videre.focus();
  }

  function hemmelighetsrad(etikett: string, verdi: string, hjelp: string): HTMLElement {
    const rad = div('hemmelighet');
    rad.append(span('hemmelighet-etikett', etikett));
    const verdiEl = el_kode(verdi);
    rad.append(verdiEl);
    rad.append(knapp('Kopiér', () => {
      // Utklippstavla kan være blokkert (usikker opprinnelse, avslått rettighet).
      // Da må teksten fortsatt kunne merkes og kopieres for hånd — derfor står
      // den synlig, og vi sier ifra framfor å late som kopieringen gikk bra.
      const skriv = globalThis.navigator?.clipboard?.writeText;
      if (!skriv) return kvittering(document.body, 'Nettleseren tillot ikke kopiering — merk teksten og kopiér for hånd.', 'feil');
      skriv.call(globalThis.navigator.clipboard, verdi)
        .then(() => kvittering(document.body, `${etikett} kopiert`))
        .catch(() => kvittering(document.body, 'Kopiering ble avvist — merk teksten og kopiér for hånd.', 'feil'));
    }, { variant: 'stille' }));
    rad.append(p('felt-hjelp', hjelp));
    return rad;
  }
}

function el_kode(verdi: string): HTMLElement {
  const c = document.createElement('code');
  c.className = 'kodeverdi';
  c.textContent = verdi;
  // Gjør verdien merkbar med tastatur, så den kan kopieres uten mus.
  c.tabIndex = 0;
  return c;
}

/** Rolig beskjed — ikke en feilboks. En utløpt lenke er ikke brukerens feil. */
function rolig(tittel: string, brød: string, ekstra?: string): HTMLElement {
  const rot = div('reg-rolig');
  rot.append(ikon('info'));
  rot.append(h(2, 'reg-tittel', tittel));
  rot.append(p('reg-brød', brød));
  if (ekstra) rot.append(p('reg-brød reg-dempet', ekstra));
  return rot;
}
